/**
 * EraPhotoOverlay.tsx — Plan 04-04 Task 3.
 *
 * Billboard plane that renders the active vista's 2026 photograph when the
 * era toggle has flipped to '2026'. Opacity damps 0↔1 with the same
 * crossfade half-life as EraGroup so the "time warp" feels coherent.
 *
 * Source: 04-RESEARCH.md Example 3 (lines 650-681) + Pattern 2 (damp).
 *
 * Material choices (AESTHETIC-BRIEF + ERA-03 + 04-RESEARCH.md):
 *   - `meshBasicMaterial`, NOT `meshStandardMaterial`. Period photos are
 *     pre-lit in their source capture; applying warm-ochre scene lighting
 *     would darken them incorrectly. Basic material ignores scene lights.
 *   - `toneMapped={false}` so the R3F Canvas's tone mapper doesn't re-tint
 *     the photograph with the scene's warm cast.
 *   - `transparent={true}` + `depthWrite={false}` permanently. Unlike
 *     EraGroup materials (which gate depthWrite on opacity threshold),
 *     this billboard is a foreground overlay — writing depth would hide
 *     the scene behind it. Treating it like a UI overlay is correct.
 *
 * Hook-ordering discipline:
 *   React Rules of Hooks require useTexture to be called unconditionally
 *   every render. We always request a texture — when there's no active
 *   zone we fall back to the first vista's photo URL (dam-2026.jpg is a
 *   guaranteed file). The mesh itself is gated by `target > 0 || maxOpacity > 0`
 *   inside the damp loop so the cost is just a cached texture lookup.
 *
 * Why NOT write eraTransitioning from here:
 *   EraGroup already owns that flag (Plan 04-02). Writing from two places
 *   creates oscillation when their damps complete at slightly different
 *   frames.
 */
import { Billboard, useTexture } from '@react-three/drei';
import { useFrame } from '@react-three/fiber';
import { useMemo, useRef } from 'react';
import * as THREE from 'three';
import { useSceneStore } from '../store/useSceneStore';
import { useManifest } from '../content/useManifest';
import { dampToTarget, CROSSFADE_HALF_LIFE_MS } from '../era/controller';

// Billboard dimensions: 3:2 landscape to match the placeholder JPG aspect
// (1024×683). 7m × 4.67m fills the horizontal FOV at ~5m camera distance
// under a 70° FOV — see 04-RESEARCH.md Pattern 3 horizontal-FOV calc.
const PLANE_WIDTH = 7;
const PLANE_HEIGHT = 4.67;
// Fallback texture URL — dam-2026.jpg is guaranteed present (Task 1 committed
// it). Keeps useTexture unconditional so Rules of Hooks are preserved.
const FALLBACK_TEXTURE_URL = '/era-photos/dam-2026.jpg';

export function EraPhotoOverlay(): React.JSX.Element | null {
  const era = useSceneStore((s) => s.era);
  const currentVistaId = useSceneStore((s) => s.currentVistaId);
  const { manifest } = useManifest();
  const materialRef = useRef<THREE.MeshBasicMaterial>(null);

  // Resolve the active zone once per currentVistaId / manifest change
  const activeZone = useMemo(() => {
    if (!manifest?.vistaZones || !currentVistaId) return null;
    return manifest.vistaZones.find((v) => v.id === currentVistaId) ?? null;
  }, [manifest, currentVistaId]);

  // Target opacity: visible iff era='2026' AND we have an active zone
  const shouldShow = era === '2026' && activeZone !== null;

  // Unconditional useTexture — Rules of Hooks. Use the active zone's photo
  // URL when there is one, otherwise the guaranteed fallback file.
  const textureUrl = activeZone ? `/${activeZone.eraPhoto}` : FALLBACK_TEXTURE_URL;
  const texture = useTexture(textureUrl);

  useFrame((_, delta) => {
    const mat = materialRef.current;
    if (!mat) return;
    const target = shouldShow ? 1 : 0;
    mat.opacity = dampToTarget(mat.opacity, target, CROSSFADE_HALF_LIFE_MS, delta);
    // Ensure these defaults survive any hot-reload or ref remount
    mat.transparent = true;
    mat.depthWrite = false;
    mat.toneMapped = false;
  });

  // Position: active zone's position, or a neutral origin when nothing active
  const position: [number, number, number] = activeZone
    ? activeZone.position
    : [0, 1.7, 0];

  return (
    <Billboard position={position} follow={true}>
      <mesh>
        <planeGeometry args={[PLANE_WIDTH, PLANE_HEIGHT]} />
        <meshBasicMaterial
          ref={(mat) => {
            materialRef.current = mat;
            // Set initial opacity=0 exactly once at material mount — this
            // guarantees the fade-in starts from 0 regardless of React's
            // JSX-prop diffing behavior (which would otherwise see
            // `opacity={0}` as unchanged and skip applying it after the
            // first render, causing materials to mount at Three.js default
            // opacity=1).
            if (mat) mat.opacity = 0;
          }}
          map={texture}
          toneMapped={false}
          transparent
          depthWrite={false}
        />
      </mesh>
    </Billboard>
  );
}
